/**
 * Participant mode — /apps/session/:sessionId
 *
 * Renders the existing responsive participant flow (waiting room → brief →
 * room → results) full-screen. This is the URL the invite link shares; it
 * works on phones, tablets, and desktops. Mirrors the B2C-safe
 * SessionRoomPage, scoped to the app's route family.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ParticipantSessionFlow } from '@/features/sessions-participant';
import { useSessionStore } from '@/stores/prioritization/sessionStore';

export default function JoinScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/apps/rank') ? '/apps/rank' : '/apps/session';
  const fetchSessionById = useSessionStore((s) => s.fetchSessionById);
  const getSession = useSessionStore((s) => s.getSession);
  const [checked, setChecked] = useState(false);
  const [exists, setExists] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!sessionId || hasFetched.current) return;
    hasFetched.current = true;
    if (getSession(sessionId)) {
      setExists(true);
      setChecked(true);
      return;
    }
    void fetchSessionById(sessionId).then((ok) => {
      setExists(ok);
      setChecked(true);
    });
  }, [sessionId, fetchSessionById, getSession]);

  if (!sessionId) return null;

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[var(--bg)]">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!exists) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gray-50 px-6 text-center dark:bg-[var(--bg)]">
        <p className="font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
          This session link doesn't work anymore
        </p>
        <p className="max-w-sm font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
          It may have been deleted, or the link was copied incompletely. Ask the
          host for a fresh link.
        </p>
        <Link to={basePath} className="font-body text-sm text-primary-600 underline">
          Back to {basePath === '/apps/rank' ? 'Rank' : 'your sessions'}
        </Link>
      </div>
    );
  }

  return (
    <ParticipantSessionFlow
      sessionId={sessionId}
      shell="standalone"
      backPath={basePath}
    />
  );
}
